import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { I18N_KEYS } from '@/language/keys';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { NodeId } from '@/designer/designer';
import ConfirmationDialog from '../Common/ConfirmationDialogV2';
import { useFlowView } from '../Recipe/FlowContext';

interface DeleteGroupDialogProps {
  groupNodeIds: NodeId[];
  onConfirm: () => void;
  closeOverlayDialog: () => void;
}

function DeleteGroupDialog({ groupNodeIds, onConfirm, closeOverlayDialog }: DeleteGroupDialogProps) {
  const { t } = useTranslation();
  const flowView = useFlowView();

  const { getItem, setItem } = useLocalStorage<boolean>('allowDeleteGroupWithoutWarning');

  const groupCount = useMemo(() => groupNodeIds.length, [groupNodeIds]);
  const groupName = useMemo(() => {
    if (groupNodeIds.length === 0) return '';
    return flowView.getNode(groupNodeIds[0])?.data?.name ?? '';
  }, [groupNodeIds, flowView]);

  const handleConfirm = useCallback(
    (checkboxChecked?: boolean) => {
      setItem(checkboxChecked ?? false);
      onConfirm();
      closeOverlayDialog();
    },
    [setItem, onConfirm, closeOverlayDialog],
  );

  const getTitle = useCallback(() => {
    if (groupCount === 1) {
      return t(I18N_KEYS.RECIPE_MAIN.FLOW.GROUP.DELETE_GROUP_CONFIRMATION.TITLE_SINGLE);
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.GROUP.DELETE_GROUP_CONFIRMATION.TITLE_PLURAL);
  }, [t, groupCount]);

  const getMessage = useCallback(() => {
    if (groupCount === 1) {
      return t(I18N_KEYS.RECIPE_MAIN.FLOW.GROUP.DELETE_GROUP_CONFIRMATION.MESSAGE_SINGLE, {
        groupName,
      });
    }
    return t(I18N_KEYS.RECIPE_MAIN.FLOW.GROUP.DELETE_GROUP_CONFIRMATION.MESSAGE_PLURAL, {
      count: groupCount,
    });
  }, [t, groupCount, groupName]);

  return (
    groupNodeIds.length > 0 && (
      <ConfirmationDialog
        open={true}
        onClose={closeOverlayDialog}
        onConfirm={handleConfirm}
        title={getTitle()}
        message={getMessage()}
        withCheckbox={true}
        checkboxLabel={t(I18N_KEYS.RECIPE_MAIN.FLOW.GROUP.DELETE_GROUP_CONFIRMATION.CHECKBOX_LABEL)}
        checkboxChecked={getItem() ?? false}
        titleSx={{ pb: 1 }}
        contentSx={{ pt: 0 }}
      />
    )
  );
}

export default DeleteGroupDialog;
